"use client";
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";

interface SlideItem {
  image: string;
  link: string | null;
}

interface BannerClientProps {
  companyId: string;
  keywords: string[];
  merchants: any[];
  slides: SlideItem[];
}

export default function BannerClient({
  companyId,
  keywords,
  merchants,
  slides,
}: BannerClientProps) {
  const [bubbles, setBubbles] = useState<any[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const displayTags = keywords?.slice(0, 6) ?? [];

  // Auto Slider Timer
  useEffect(() => {
    if (!slides || slides.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 4000);

    return () => clearInterval(timer);
  }, [slides]);

  // Merchant Bubbles Logic
  useEffect(() => {
    if (merchants && merchants.length > 0) {
      const selected = merchants.slice(0, 8).map((m, i) => ({
        name: m.merchant_name,
        logo: m.merchant_logo,
        left: `${i * 12 + 5}%`,
        delay: i * 2,
        duration: 10 + Math.random() * 5,
      }));
      setBubbles(selected);
    }
  }, [merchants]);

  const activeSlide = slides[currentSlide];

  // 🛠️ DEBUGGING HELPER: Apne console (F12) me check karein ke image ka actual path kya aa rha hai
  useEffect(() => {
    if (activeSlide) {
      console.log("Current Active Slide Image Path:", activeSlide.image);
    }
  }, [activeSlide]);

  // 🛠️ SAFE URL RESOLVER: Agar images public folder me hain toh theek, warna agar backend se hain toh yahan apna domain laga dain
  const getImageUrl = (srcString: string) => {
    if (!srcString) return "/placeholder.jpg"; // Koi bhi fallback image path
    if (srcString.startsWith("http://") || srcString.startsWith("https://") || srcString.startsWith("/")) {
      return srcString;
    }
    // Agar API se relative path aa raha hai (e.g. "uploads/banner.jpg"), toh apna backend domain yahan attach karein:
    return `https://your-backend-api-domain.com/${srcString}`;
  };

  return (
    <div className="relative z-10 max-w-7xl mx-auto w-full px-6 flex flex-col gap-8 pt-6 md:pt-10 pb-4">
      {/* Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[0.9fr,1.4fr] gap-10 items-center text-center lg:text-left w-full">
        {/* Left Side: Text Content & Buttons */}
        <div className="flex flex-col items-center lg:items-start lg:pr-4 order-2 lg:order-1">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl lg:text-5xl font-black mb-4 leading-tight tracking-tighter text-[#1A1A1A]"
          >
            The future of <br />
            <span className="text-[#800000]">smart savings.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-base text-[#0D0D0D]/60 mb-6 max-w-md leading-relaxed font-medium italic"
          >
            Discover elite coupon codes and deals from 15,000+ brands. Verified
            daily. Saved instantly.
          </motion.p>

          <div className="flex flex-row gap-4 w-full sm:w-auto px-4 sm:px-0 items-center lg:items-start justify-center lg:justify-start">
            <Link
              href="/#recent-deals-section"
              className="w-fit sm:w-auto px-7 py-3 bg-[#800000] text-[#FFFDF5] rounded-full font-black text-[10px] uppercase tracking-[0.2em] hover:scale-105 transition-all shadow-lg active:scale-95 text-center no-underline"
            >
              Recent Deals
            </Link>
            <Link
              href={"/category"}
              className="w-fit sm:w-auto no-underline px-7 py-3 bg-[#0D0D0D] text-[#FFFDF5] rounded-full font-black text-[10px] uppercase tracking-[0.2em] hover:bg-transparent hover:text-[#0D0D0D] border border-[#0D0D0D] transition-all active:scale-95 text-center"
            >
              Categories
            </Link>
          </div>
        </div>

        {/* Right Side: Dynamic Cross-fading Slider */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative w-full aspect-[8/3] lg:aspect-[8/3] z-10 order-1 lg:order-2"
        >
          <div className="absolute inset-0 bg-[#800000]/5 rounded-[2.5rem] -rotate-2 translate-x-3 translate-y-2"></div>

          <div className="absolute inset-0 rounded-[2.5rem] overflow-hidden border border-[#D1C7A7] shadow-2xl bg-white">
            <AnimatePresence mode="wait">
              {activeSlide?.image && (
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.2, ease: "easeInOut" }}
                  className="absolute inset-0 h-full w-full relative"
                >
                  {activeSlide?.link ? (
                    <Link
                      href={activeSlide.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full h-full relative cursor-pointer"
                    >
                      <Image
                        src={getImageUrl(activeSlide.image)}
                        fill
                        className="object-cover"
                        alt={`Dynamic Slider Item ${currentSlide}`}
                        priority
                        unoptimized
                      />
                    </Link>
                  ) : (
                    <Image
                      src={getImageUrl(activeSlide.image)}
                      fill
                      className="object-cover"
                      alt={`Slider Item ${currentSlide}`}
                      priority
                      unoptimized
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pagination Dots */}
            {slides.length > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
                {slides.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1.5 transition-all duration-300 rounded-full ${
                      idx === currentSlide
                        ? "w-6 bg-[#800000]"
                        : "w-1.5 bg-white/50"
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Live Badge */}
            <div className="absolute top-6 left-6 z-20 inline-flex items-center gap-2 bg-black/30 backdrop-blur-md border border-white/20 rounded-full px-4 py-1.5">
              <span className="flex h-2 w-2 rounded-full bg-[#800000] animate-pulse"></span>
              <span className="text-[9px] font-black text-white uppercase tracking-[0.2em]">
                Live Premium Discounts
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Trending Keywords Section */}
      {displayTags.length > 0 && (
        <div className="w-full mt-6 text-center flex flex-col items-center justify-center px-4 sm:px-0">
          {/* Header Box & Text (Perfectly Centered) */}
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-10 h-10 bg-[#800000]/5 rounded-xl flex items-center justify-center text-[#800000] border border-[#800000]/10 shadow-sm">
              <svg
                className="w-4 h-4 transform -rotate-45"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                <line x1="7" y1="7" x2="7.01" y2="7"></line>
              </svg>
            </div>
            <h2 className="text-xl md:text-2xl font-black text-[#1A1A1A] tracking-tight uppercase m-0 flex items-center gap-2">
              TRENDING{" "}
              <span className="font-serif italic lowercase text-[#800000] font-normal tracking-normal normal-case">
                merchants
              </span>
            </h2>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-2.5 max-w-4xl w-full">
            {displayTags.map((tag, i) => (
              <Link
                key={i}
                href={`/search?query=${encodeURIComponent(tag)}`}
                className="px-4 py-3 bg-[#800000]/10 backdrop-blur-sm border border-[#800000] text-[#800000] rounded-full shadow-sm hover:shadow-md hover:border-white/90 hover:bg-[#800000] hover:text-white transition-all duration-300 hover:-translate-y-0.5 transform no-underline group/tag flex items-center gap-1.5"
              >
                <span className="text-[10px] font-bold uppercase tracking-wider transition-colors">
                  {tag}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}